// Auth controller

import { Request } from "@asanrom/request-browser";
import { Timeouts } from "@/utils/timeout";
import { AppEvents } from "./app-events";
import { addRequestAuthenticationHandler } from "@asanrom/request-browser";
import { fetchFromLocalStorage, saveIntoLocalStorage } from "@/utils/local-storage";
import { ApiAuth } from "@/api/api-group-auth";
import { HOME_ROUTE } from "@/app-events-plugin";
import { setCookie } from "@/utils/cookie";
import { GatewayIdentityContext, LLMSidecarStatus } from "@/api/definitions";

const SESSION_TOKEN_HEADER_NAME = "x-session-id";

export class AuthController {
    public static Status: "UNAUTHORIZED" | "LOGGED_IN" | "USER_NOT_FOUND" | "TFA_REQUIRED" = "UNAUTHORIZED";

    public static UID = "";
    public static Role = "";
    public static Permissions = [];
    public static Username = "";
    public static Email = "";

    public static PageToGo: { name: string; params?: any; query?: any } = { name: HOME_ROUTE };

    public static RequiresTwoFactorAuthentication = false;

    public static Session = "";

    public static Locale = "";

    public static ProfileName = "";
    public static ProfileImage = "";

    public static PlatformLogo = "";
    public static PlatformLogoCompact = "";
    public static PlatformFavicon = "";
    public static FooterEnabled = true;
    public static FooterMode = "single_image";
    public static FooterImage = "";
    public static FooterLogos: { id: string; url: string; size?: number; format?: string; date?: number }[] = [];

    public static Loading = true;

    public static FirstTimeLoaded = false;

    public static EmailRegisterEnabled = false;

    public static ClearingHouseEnabled = false;
    public static IsBroker = false;
    public static IsAttestator = false;

    public static GaiaX = false;

    public static DcatApEsEnabled = false;
    public static DcatApEsActivated = false;
    public static DcatApEsPublisherDefault = "";
    public static DcatApEsDefaultLicense = "";

    public static GatewayIdentity: GatewayIdentityContext = {
        id: "",
        method: "",
        availableMethods: [],
        enabledMethods: [],
    };

    public static SidecarLLM: LLMSidecarStatus = {
        available: false,
        rag: false,
        assistant: false,
    };

    public static Initialize() {
        addRequestAuthenticationHandler(() => {
            const authHeaders = Object.create(null);
            authHeaders[SESSION_TOKEN_HEADER_NAME] = AuthController.Session;

            return authHeaders;
        });

        AppEvents.AddEventListener("unauthorized", AuthController.ClearSession);
        AuthController.Session = fetchFromLocalStorage("x-session-token", "");
        AuthController.CheckAuthStatus();
    }

    public static isAuthenticated(): boolean {
        return AuthController.Status === "LOGGED_IN";
    }

    public static isAskingForTwoFactor(): boolean {
        return AuthController.Status === "TFA_REQUIRED";
    }

    public static hasPermission(permission: string): boolean {
        return AuthController.Permissions.includes(permission);
    }

    public static isDcatApEsEnabled(): boolean {
        return AuthController.DcatApEsEnabled;
    }

    public static getDcatApEsPublisherDefault(): string {
        return AuthController.DcatApEsPublisherDefault;
    }

    public static getDcatApEsDefaultLicense(): string {
        return AuthController.DcatApEsDefaultLicense;
    }

    public static setDcatBridge(state: boolean) {
        this.DcatApEsActivated = state;
        saveIntoLocalStorage("dcat-bridge-state", state);
    }

    public static getDcatBridgeConfig(): boolean {
        this.DcatApEsActivated = fetchFromLocalStorage("dcat-bridge-state", false);
        return this.DcatApEsActivated;
    }

    public static CheckAuthStatus() {
        AuthController.Loading = true;
        AppEvents.Emit("auth-status-loading", true);
        Timeouts.Abort("auth-control-check");
        Request.Pending("auth-control-check", ApiAuth.GetContext())
            .onSuccess((response) => {
                AuthController.Status = response.status;
                AuthController.UID = response.uid;
                AuthController.Role = response.role;
                AuthController.Permissions = response.permissions.slice();
                AuthController.Username = response.username;
                AuthController.Email = response.email;
                AuthController.RequiresTwoFactorAuthentication = response.tfa;
                AuthController.Loading = false;
                AuthController.ProfileName = response.profileName;
                AuthController.ProfileImage = response.profileImage;
                AuthController.Locale = response.locale;
                AuthController.FirstTimeLoaded = true;
                AuthController.EmailRegisterEnabled = response.canRegisterWithEmail || false;
                AuthController.ClearingHouseEnabled = response.clearingHouseEnabled || false;
                AuthController.IsBroker = response.isMetadataBroker || false;
                AuthController.IsAttestator = response.isAttestator || false;
                AuthController.GatewayIdentity = response.gatewayIdentity || AuthController.GatewayIdentity;
                AuthController.GaiaX = response.gx || false;
                AuthController.PlatformLogo = response.platformLogo || "";
                AuthController.PlatformLogoCompact = response.platformLogoCompact || "";
                AuthController.PlatformFavicon = response.platformFavicon || "";
                AuthController.applyFavicon();
                AuthController.FooterEnabled = !!response.footerEnabled;
                AuthController.FooterMode = response.footerMode || "single_image";
                AuthController.FooterImage = response.footerImage || "";
                AuthController.FooterLogos = response.footerLogos || [];
                AuthController.SidecarLLM = response.llm || { available: false, rag: false, assistant: false };
                AuthController.DcatApEsEnabled = response.dcatApEsEnabled || false;
                AuthController.DcatApEsPublisherDefault = response.dcatApEsPublisherDefault || "";
                AuthController.DcatApEsDefaultLicense = response.dcatApEsDefaultLicense || "";
                setCookie("session_id", AuthController.Session);
                AppEvents.Emit("auth-status-changed");
                AppEvents.Emit("auth-status-loading", false);
            })
            .onRequestError((err, handleErr) => {
                handleErr(err, {
                    unauthorized: () => {
                        AuthController.Status = "UNAUTHORIZED";
                        AuthController.UID = "";
                        AuthController.Username = "";
                        AuthController.Permissions = [];
                        AuthController.Loading = false;
                        AppEvents.Emit("auth-status-changed");
                        AppEvents.Emit("auth-status-loading", false);
                    },
                    temporalError: () => {
                        Timeouts.Set("auth-control-check", 1500, AuthController.CheckAuthStatus);
                    },
                });
            })
            .onUnexpectedError((err) => {
                console.error(err);
                // We assume the credentials are invalid
                AuthController.Status = "UNAUTHORIZED";
                AuthController.UID = "";
                AuthController.Permissions = [];
                AuthController.Username = "";
                AuthController.Loading = false;
                AppEvents.Emit("auth-status-changed");
                AppEvents.Emit("auth-status-loading", false);
            });
    }

    public static ClearSession() {
        if (!AuthController.Session) {
            return;
        }

        AuthController.Status = "UNAUTHORIZED";
        AuthController.Session = "";

        saveIntoLocalStorage("x-session-token", "");

        AuthController.UID = "";
        AuthController.Username = "";
        AuthController.Permissions = [];

        AppEvents.Emit("auth-status-changed");
    }

    public static SetSession(session: string) {
        AuthController.Status = "UNAUTHORIZED";
        AuthController.Session = session;
        AuthController.UID = "";
        AuthController.Username = "";
        AuthController.Permissions = [];
        saveIntoLocalStorage("x-session-token", session);
        AppEvents.Emit("auth-status-changed");
        AuthController.CheckAuthStatus();
    }

    public static Logout(): Promise<void> {
        const currentSession = AuthController.Session;
        return new Promise<void>((resolve) => {
            Request.Do(ApiAuth.Logout())
                .onSuccess(() => {
                    if (AuthController.Session === currentSession) {
                        AuthController.ClearSession();
                    }
                    resolve();
                })
                .onRequestError(() => {
                    if (AuthController.Session === currentSession) {
                        AuthController.ClearSession();
                    }
                    resolve();
                })
                .onUnexpectedError((err) => {
                    console.error(err);
                    if (AuthController.Session === currentSession) {
                        AuthController.ClearSession();
                    }
                    resolve();
                });
        });
    }

    public static applyFavicon() {
        const link = document.querySelector("link[rel='icon']") as HTMLLinkElement;
        if (link) {
            link.href = AuthController.PlatformFavicon || "/favicon.ico";
        }
    }
}
